import React from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';

/**
 * TourOutline - A sidebar component that displays tour progress
 * Shows step list with completion status and allows navigation between steps
 *
 * @param {Array} steps - Array of tour steps with title property
 * @param {number} currentStep - Current step index (0-based)
 * @param {Array} completedSteps - Array of completed step indices
 * @param {function} onStepClick - Callback when a step is clicked
 * @param {string} title - Custom title for the tour progress sidebar
 */
const TourOutline = ({ steps = [], currentStep = 0, completedSteps = [], onStepClick, title = "Tour Progress" }) => {
  if (!steps || steps.length === 0) {
    return null;
  }

  const getStepIcon = (index) => {
    if (completedSteps.includes(index)) {
      return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />;
    }
    if (index === currentStep) {
      return <PlayCircleFilledIcon sx={{ color: 'primary.main', fontSize: 20 }} />;
    }
    return <RadioButtonUncheckedIcon sx={{ color: 'text.disabled', fontSize: 20 }} />;
  };

  const getStepTitle = (step, index) => {
    // Extract title from step object
    if (typeof step.title === 'string') {
      return step.title;
    }
    return `Step ${index + 1}`;
  };

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        left: 20,
        bottom: 20,
        width: 220,
        maxHeight: '70vh',
        overflow: 'auto',
        zIndex: 10001,
        borderRadius: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
        <Typography variant="caption">
          Step {currentStep + 1} of {steps.length}
        </Typography>
      </Box>

      <List sx={{ py: 1 }}>
        {steps.map((step, index) => {
          const isCompleted = completedSteps.includes(index);
          const isCurrent = index === currentStep;

          return (
            <ListItem key={index} disablePadding>
              <ListItemButton
                onClick={() => onStepClick && onStepClick(index)}
                selected={isCurrent}
                sx={{
                  py: 1,
                  '&.Mui-selected': {
                    bgcolor: 'primary.light',
                    '&:hover': {
                      bgcolor: 'primary.light',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {getStepIcon(index)}
                </ListItemIcon>
                <ListItemText
                  primary={getStepTitle(step, index)}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: isCurrent ? 600 : 400,
                    color: isCompleted ? 'text.secondary' : 'text.primary',
                    sx: {
                      textDecoration: isCompleted ? 'line-through' : 'none',
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Box
        sx={{
          p: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Click a step to navigate
        </Typography>
      </Box>
    </Paper>
  );
};

export default TourOutline;
